import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const OPTIONS_LIMIT = 15;
const SOURCE_DOCS_PAGE_SIZE = 30;

const filterOptions = createFilterOptions({
  limit: OPTIONS_LIMIT,
  trim: true,
});

function GridTopBar(props) {
  const { rowCount, title } = props;

  return (
    <GridToolbarContainer>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f8fbff",
          borderBottom: "1px solid #dbe7f3",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={`Rows: ${rowCount}`}
          color="primary"
          variant="outlined"
          sx={{ height: 24 }}
        />
      </Box>
    </GridToolbarContainer>
  );
}

const normalizeReturnRows = (rows = []) => {
  return (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    srl: idx + 1,
    partyid: row.partyid ?? null,
    partydetails: row.partydetails || "",
    claimheaderid: row.claimheaderid ?? null,
    claimdetailid: row.claimdetailid ?? null,
    claimno: row.claimno || "",
    againstitemid: row.againstitemid ?? null,
    againstitemcode: row.againstitemcode || "",
    againstitemname: row.againstitemname || "",
    againstsrlno: row.againstsrlno || "",
    returnsrlno: String(row.returnsrlno || "").trim(),
  }));
};

const WarrantyClaimReturn = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const [disableSave, setDisableSave] = useState(false);
  const [disableUpdate, setDisableUpdate] = useState(false);

  const [claimreturndate, setClaimReturnDate] = useState(dayjs());
  const [claimreturnno, setClaimReturnNo] = useState("");
  const [mode, setMode] = useState(1);

  const [searchdisabled, setSearchDisabled] = useState(true);
  const [searchbtndisabled, setSearchBtnDisabled] = useState(true);

  const [docopts, setDocOpts] = useState([]);
  const [docdetails, setDocDetails] = useState(null);

  const [sourceRows, setSourceRows] = useState([]);
  const [returnRows, setReturnRows] = useState([]);

  const [pageLoading, setPageLoading] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);

  const [docSearch, setDocSearch] = useState("");
  const [expandedDocs, setExpandedDocs] = useState({});
  const [selectedClaimIds, setSelectedClaimIds] = useState({});

  const [sourcePage, setSourcePage] = useState(1);
  const [sourceTotalDocs, setSourceTotalDocs] = useState(0);
  const [sourceTotalPages, setSourceTotalPages] = useState(0);
  const [hasLoadedSource, setHasLoadedSource] = useState(false);

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
    return null;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      LocalstorageHelper.RemoveUserToken();
    }, 3600000);

    return () => clearTimeout(timeout);
  }, []);

  async function getDocNos() {
    try {
      const response = await fetch("/warranty/claimreturn/documentnos", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setDocOpts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getDocNos();
  }, []);

  const clearSourceSection = () => {
    setSourceRows([]);
    setDocSearch("");
    setExpandedDocs({});
    setSelectedClaimIds({});
    setSourcePage(1);
    setSourceTotalDocs(0);
    setSourceTotalPages(0);
    setHasLoadedSource(false);
  };

  const resetFormState = () => {
    setDocDetails(null);
    setClaimReturnNo("");
    setClaimReturnDate(dayjs());
    setReturnRows([]);
    clearSourceSection();
    setDisableSave(false);
    setDisableUpdate(false);
  };

  const handleModeChange = (e) => {
    const selectedMode = parseInt(e.target.value, 10);
    setMode(selectedMode);

    if (selectedMode === 1) {
      setSearchDisabled(true);
      setSearchBtnDisabled(true);
    } else {
      setSearchDisabled(false);
      setSearchBtnDisabled(false);
    }

    resetFormState();
  };

  const fetchSourceDocs = async (page = 1) => {
    try {
      setLoadingSource(true);

      const response = await fetch(
        `/warranty/claimreturn/unissued?page=${page}&pageSize=${SOURCE_DOCS_PAGE_SIZE}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        }
      );

      const jsonData = await response.json();

      if (jsonData?.type === "error") {
        toast.error(jsonData.message || "Failed to load claim documents");
        clearSourceSection();
        return;
      }

      setSourceRows(Array.isArray(jsonData?.rows) ? jsonData.rows : []);
      setSourcePage(Number(jsonData?.page || page));
      setSourceTotalDocs(Number(jsonData?.totaldocs || 0));
      setSourceTotalPages(Number(jsonData?.totalpages || 0));
      setHasLoadedSource(true);
      setExpandedDocs({});
      setSelectedClaimIds({});
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load claim documents");
      clearSourceSection();
    } finally {
      setLoadingSource(false);
    }
  };

  const groupedSourceDocs = useMemo(() => {
    const q = String(docSearch || "").trim().toLowerCase();
    const map = new Map();

    for (const row of sourceRows) {
      const dockey = String(row.claimheaderid || row.claimno || "");
      const claimno = row.claimno || "NO-DOC";

      if (!map.has(dockey)) {
        map.set(dockey, {
          dockey,
          claimheaderid: row.claimheaderid,
          claimno,
          claimdate: row.claimdate,
          items: [],
        });
      }

      map.get(dockey).items.push(row);
    }

    let docs = Array.from(map.values()).map((doc) => {
      let items = doc.items;

      if (q) {
        items = items.filter((row) => {
          return (
            String(row.claimno || "").toLowerCase().includes(q) ||
            String(row.partydetails || "").toLowerCase().includes(q) ||
            String(row.againstitemcode || "").toLowerCase().includes(q) ||
            String(row.againstitemname || "").toLowerCase().includes(q) ||
            String(row.againstsrlno || "").toLowerCase().includes(q)
          );
        });
      }

      const partyList = [
        ...new Set(
          items
            .map((x) => String(x.partydetails || "").trim())
            .filter((x) => x.length > 0)
        ),
      ];

      let partySummary = "";
      if (partyList.length === 1) {
        partySummary = partyList[0];
      } else if (partyList.length > 1) {
        partySummary = `${partyList.length} parties`;
      }

      return {
        ...doc,
        items,
        itemcount: items.length,
        partySummary,
      };
    });

    docs = docs.filter((doc) => doc.items.length > 0);

    docs.sort((a, b) => {
      const aDate = a.claimdate ? new Date(a.claimdate).getTime() : 0;
      const bDate = b.claimdate ? new Date(b.claimdate).getTime() : 0;
      if (bDate !== aDate) return bDate - aDate;
      return String(b.claimno).localeCompare(String(a.claimno));
    });

    return docs;
  }, [sourceRows, docSearch]);

  const toggleDocExpand = (dockey) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [dockey]: !prev[dockey],
    }));
  };

  const isSourceRowAlreadyAdded = (sourceRow) => {
    return returnRows.some((r) => r.claimdetailid === sourceRow.claimdetailid);
  };

  const addSourceRowToGrid = (selectedRow) => {
    if (!selectedRow) {
      toast.error("Select a claim detail first");
      return;
    }

    const alreadyExists = returnRows.some(
      (r) => r.claimdetailid === selectedRow.claimdetailid
    );

    if (alreadyExists) {
      toast.error("This claim detail is already added");
      return;
    }

    const newRow = {
      srl: returnRows.length + 1,
      partyid: selectedRow.partyid || null,
      partydetails: selectedRow.partydetails || "",
      claimheaderid: selectedRow.claimheaderid || null,
      claimdetailid: selectedRow.claimdetailid,
      claimno: selectedRow.claimno || "",
      againstitemid: selectedRow.againstitemid || null,
      againstitemcode: selectedRow.againstitemcode || "",
      againstitemname: selectedRow.againstitemname || "",
      againstsrlno: selectedRow.againstsrlno || "",
      returnsrlno: "",
    };

    setReturnRows((prev) => normalizeReturnRows([...prev, newRow]));
  };

  const addWholeDocumentToGrid = (doc) => {
    const rowsToAdd = doc.items.filter((item) => !isSourceRowAlreadyAdded(item));

    if (!rowsToAdd.length) {
      toast.info("All items from this document are already added");
      return;
    }

    const confirmed = window.confirm(
      `Add all ${rowsToAdd.length} items from document ${doc.claimno}?`
    );

    if (!confirmed) return;

    setReturnRows((prev) => {
      const mapped = rowsToAdd.map((row) => ({
        partyid: row.partyid || null,
        partydetails: row.partydetails || "",
        claimheaderid: row.claimheaderid || null,
        claimdetailid: row.claimdetailid,
        claimno: row.claimno || "",
        againstitemid: row.againstitemid || null,
        againstitemcode: row.againstitemcode || "",
        againstitemname: row.againstitemname || "",
        againstsrlno: row.againstsrlno || "",
        returnsrlno: "",
      }));

      return normalizeReturnRows([...prev, ...mapped]);
    });

    toast.success(`${rowsToAdd.length} items added from ${doc.claimno}`);
  };

  const toggleItemSelection = (row) => {
    const key = String(row.claimdetailid);
    setSelectedClaimIds((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const addSelectedCheckedItems = () => {
    const chosen = sourceRows.filter(
      (row) => selectedClaimIds[String(row.claimdetailid)]
    );

    const rowsToAdd = chosen.filter((item) => !isSourceRowAlreadyAdded(item));

    if (!rowsToAdd.length) {
      toast.info("No new selected items to add");
      return;
    }

    setReturnRows((prev) => {
      const mapped = rowsToAdd.map((row) => ({
        partyid: row.partyid || null,
        partydetails: row.partydetails || "",
        claimheaderid: row.claimheaderid || null,
        claimdetailid: row.claimdetailid,
        claimno: row.claimno || "",
        againstitemid: row.againstitemid || null,
        againstitemcode: row.againstitemcode || "",
        againstitemname: row.againstitemname || "",
        againstsrlno: row.againstsrlno || "",
        returnsrlno: "",
      }));

      return normalizeReturnRows([...prev, ...mapped]);
    });

    setSelectedClaimIds({});
    toast.success(`${rowsToAdd.length} selected items added`);
  };

  const getDocSelectionState = (doc) => {
    const ids = doc.items.map((x) => String(x.claimdetailid));
    const selectedCount = ids.filter((id) => selectedClaimIds[id]).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === ids.length) return "all";
    return "partial";
  };

  const toggleWholeDocSelection = (doc) => {
    const ids = doc.items.map((x) => String(x.claimdetailid));
    const state = getDocSelectionState(doc);

    setSelectedClaimIds((prev) => {
      const next = { ...prev };

      if (state === "all") {
        ids.forEach((id) => {
          delete next[id];
        });
      } else {
        ids.forEach((id) => {
          next[id] = true;
        });
      }

      return next;
    });
  };

  const handleDeleteClick = (id) => () => {
    if (window.confirm("Are you sure you want to remove the item?")) {
      setReturnRows((prevRows) =>
        normalizeReturnRows(prevRows.filter((row) => row.srl !== id))
      );
    }
  };

  const handleReturnSrlChange = (id, value) => {
    setReturnRows((prevRows) =>
      prevRows.map((row) =>
        row.srl === id ? { ...row, returnsrlno: String(value || "") } : row
      )
    );
  };

  const getLatestGridRows = () => {
    return normalizeReturnRows(returnRows);
  };

  const returnColumns = useMemo(
    () => [
      {
        field: "action",
        type: "actions",
        headerClassName: "super-app-theme--header",
        headerName: "",
        width: 50,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(params.id)}
            color="inherit"
          />,
        ],
      },
      {
        field: "srl",
        headerName: "Srl",
        width: 55,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "partydetails",
        headerName: "Party",
        width: 240,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "claimno",
        headerName: "Claim No",
        width: 150,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstitemcode",
        headerName: "Against Part",
        width: 150,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstitemname",
        headerName: "Description",
        width: 220,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstsrlno",
        headerName: "Claim Srl No",
        width: 150,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "returnsrlno",
        headerName: "Return Srl No",
        width: 220,
        sortable: false,
        filterable: false,
        headerClassName: "super-app-theme--header",
        renderCell: (params) => (
          <TextField
            variant="standard"
            value={params.row.returnsrlno || ""}
            onChange={(e) =>
              handleReturnSrlChange(params.row.srl, e.target.value)
            }
            fullWidth
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: 12,
                px: 0.5,
              },
            }}
            inputProps={{
              style: {
                fontSize: 12,
                padding: "2px 0",
              },
            }}
          />
        ),
      },
    ],
    [returnRows]
  );

  const handleSave = async (e) => {
    e.preventDefault();

    const latestRows = getLatestGridRows();

    if (!latestRows.length) {
      toast.error("Add at least one row");
      return;
    }

    for (const r of latestRows) {
      if (!r.claimdetailid) {
        toast.error(`Claim detail missing at row ${r.srl}`);
        return;
      }
      if (!String(r.returnsrlno || "").trim()) {
        toast.error(`Return serial missing at row ${r.srl}`);
        return;
      }
    }

    setDisableSave(true);
    setPageLoading(true);

    try {
      setReturnRows(latestRows);

      const body = {
        claimreturndate,
        rows: latestRows,
      };

      const response = await fetch("/warranty/claimreturn/insert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Save failed");
        setDisableSave(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Save failed");
      setDisableSave(false);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!docdetails?.claimreturnheaderid) {
      toast.error("Select document");
      return;
    }

    setPageLoading(true);

    try {
      const response = await fetch(
        `/warranty/claimreturn/data/${docdetails.claimreturnheaderid}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        }
      );

      const jsonData = await response.json();

      if (jsonData.type === "error") {
        toast.error(jsonData.message || "Search failed");
        return;
      }

      setReturnRows(normalizeReturnRows(jsonData.detaildata || []));
      setClaimReturnDate(dayjs(jsonData?.headerdata?.claimreturndate));
      setClaimReturnNo(jsonData?.headerdata?.claimreturnno || "");
      clearSourceSection();
    } catch (err) {
      console.log(err.message);
      toast.error("Search failed");
    } finally {
      setPageLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!docdetails?.claimreturnheaderid) {
      toast.error("Select document");
      return;
    }

    const latestRows = getLatestGridRows();

    if (!latestRows.length) {
      toast.error("No rows to update");
      return;
    }

    for (const r of latestRows) {
      if (!r.claimdetailid) {
        toast.error(`Claim detail missing at row ${r.srl}`);
        return;
      }
      if (!String(r.returnsrlno || "").trim()) {
        toast.error(`Return serial missing at row ${r.srl}`);
        return;
      }
    }

    setDisableUpdate(true);
    setPageLoading(true);

    try {
      setReturnRows(latestRows);

      const body = {
        claimreturndate,
        rows: latestRows,
      };

      const response = await fetch(
        `/warranty/claimreturn/update/${docdetails.claimreturnheaderid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            sptoken: userToken,
          },
          body: JSON.stringify(body),
        }
      );

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Update failed");
        setDisableUpdate(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Update failed");
      setDisableUpdate(false);
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <>
      <Box>
        <Sidebar
          Title={"WARRANTY CLAIM RETURN"}
          expandedPanel={"panel2"}
          children={
            <Box component="form" sx={{ width: "100%" }} autoComplete="off">
              <Backdrop
                open={pageLoading || loadingSource}
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 999,
                  backdropFilter: "blur(2px)",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={28} />
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                  {loadingSource
                    ? "Loading claim documents..."
                    : "Processing..."}
                </Typography>
              </Backdrop>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: "10px 14px !important" }}>
                  <Stack
                    spacing={1}
                    direction={{ xs: "column", lg: "row" }}
                    alignItems={{ xs: "stretch", lg: "center" }}
                  >
                    <TextField
                      select
                      label="Mode"
                      value={mode}
                      onChange={handleModeChange}
                      sx={{ width: 110 }}
                      size="small"
                    >
                      <MenuItem value={1}>New</MenuItem>
                      <MenuItem value={2}>Edit</MenuItem>
                    </TextField>

                    <TextField
                      disabled
                      label="Return No"
                      value={claimreturnno}
                      sx={{ width: 160 }}
                      size="small"
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Return Date"
                        inputFormat="DD/MM/YYYY"
                        value={claimreturndate}
                        onChange={(newValue) => {
                          setClaimReturnDate(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 150 }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Stack>

                  {mode === 2 && (
                    <>
                      <Divider sx={{ my: 1.25 }} />
                      <Stack
                        spacing={1}
                        direction={{ xs: "column", lg: "row" }}
                        alignItems={{ xs: "stretch", lg: "center" }}
                      >
                        <Autocomplete
                          disabled={searchdisabled}
                          value={docdetails}
                          filterOptions={filterOptions}
                          onChange={(event, newValue) => setDocDetails(newValue)}
                          options={docopts}
                          getOptionLabel={(option) => option.claimreturnno || ""}
                          sx={{ minWidth: 250, flex: 1 }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              placeholder="Search Claim Return"
                              label="Return Document"
                            />
                          )}
                        />

                        <Button
                          disabled={searchbtndisabled}
                          variant="contained"
                          onClick={handleSearch}
                          startIcon={<SearchRoundedIcon />}
                          sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                        >
                          Search
                        </Button>
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: "10px 14px !important" }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <DescriptionOutlinedIcon fontSize="small" color="primary" />
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        Unissued Claim Documents
                      </Typography>
                      <Chip
                        size="small"
                        label={`Total Docs: ${hasLoadedSource ? sourceTotalDocs : 0}`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`Visible Docs: ${groupedSourceDocs.length}`}
                        variant="outlined"
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <TextField
                        size="small"
                        label="Search"
                        placeholder="Claim / party / part / serial"
                        value={docSearch}
                        onChange={(e) => setDocSearch(e.target.value)}
                        sx={{ width: 280 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloudDownloadRoundedIcon />}
                        onClick={() => fetchSourceDocs(1)}
                        sx={{ minWidth: 130 }}
                      >
                        Load Docs
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={addSelectedCheckedItems}
                        sx={{ minWidth: 140 }}
                      >
                        Add Checked
                      </Button>
                    </Stack>
                  </Stack>

                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 360,
                      overflow: "auto",
                      borderRadius: 2,
                      bgcolor: "#fcfdff",
                    }}
                  >
                    {!hasLoadedSource ? (
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          Press <b>Load Docs</b> to populate claim documents.
                        </Typography>
                      </Box>
                    ) : groupedSourceDocs.length === 0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          No claim items found.
                        </Typography>
                      </Box>
                    ) : (
                      groupedSourceDocs.map((doc) => {
                        const expanded = !!expandedDocs[doc.dockey];
                        const docSelectionState = getDocSelectionState(doc);
                        const alreadyAddedCount = doc.items.filter((x) =>
                          isSourceRowAlreadyAdded(x)
                        ).length;

                        return (
                          <Box
                            key={doc.dockey}
                            sx={{
                              borderBottom: "1px solid #edf2f7",
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <Box
                              sx={{
                                px: 1,
                                py: 0.75,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: expanded ? "#f7fbff" : "transparent",
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => toggleDocExpand(doc.dockey)}
                              >
                                {expanded ? (
                                  <ExpandMoreRoundedIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightRoundedIcon fontSize="small" />
                                )}
                              </IconButton>

                              <Checkbox
                                size="small"
                                checked={docSelectionState === "all"}
                                indeterminate={docSelectionState === "partial"}
                                onChange={() => toggleWholeDocSelection(doc)}
                              />

                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                    {doc.claimno}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={`${doc.itemcount} item${
                                      doc.itemcount > 1 ? "s" : ""
                                    }`}
                                    sx={{ height: 22 }}
                                  />
                                  {doc.partySummary ? (
                                    <Chip
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      label={doc.partySummary}
                                      sx={{ height: 22, maxWidth: 280 }}
                                    />
                                  ) : null}
                                  {alreadyAddedCount > 0 && (
                                    <Chip
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      label={`${alreadyAddedCount} already added`}
                                      sx={{ height: 22 }}
                                    />
                                  )}
                                </Stack>
                              </Box>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => addWholeDocumentToGrid(doc)}
                                sx={{ minWidth: 110 }}
                              >
                                Add Doc
                              </Button>
                            </Box>

                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 1.5, pb: 1 }}>
                                {doc.items.map((item) => {
                                  const checked =
                                    !!selectedClaimIds[String(item.claimdetailid)];
                                  const alreadyAdded = isSourceRowAlreadyAdded(item);

                                  return (
                                    <Box
                                      key={item.claimdetailid}
                                      sx={{
                                        px: 1,
                                        py: 0.75,
                                        ml: 4.5,
                                        mb: 0.5,
                                        borderRadius: 1.5,
                                        bgcolor: alreadyAdded ? "#f5f5f5" : "#ffffff",
                                        border: "1px solid #eef3f8",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={() => toggleItemSelection(item)}
                                        disabled={alreadyAdded}
                                      />

                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                          sx={{
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {item.againstitemcode} - {item.againstitemname}
                                        </Typography>
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          flexWrap="wrap"
                                          useFlexGap
                                          sx={{ mt: 0.25 }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 11.5,
                                              color: "text.secondary",
                                            }}
                                          >
                                            Claim Srl: {item.againstsrlno}
                                          </Typography>
                                          {item.partydetails && (
                                            <Typography
                                              sx={{
                                                fontSize: 11.5,
                                                color: "text.secondary",
                                              }}
                                            >
                                              Party: {item.partydetails}
                                            </Typography>
                                          )}
                                        </Stack>
                                      </Box>

                                      {alreadyAdded ? (
                                        <Chip
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          label="Added"
                                          sx={{ height: 22 }}
                                        />
                                      ) : (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={<BoltRoundedIcon />}
                                          onClick={() => addSourceRowToGrid(item)}
                                          sx={{ minWidth: 90 }}
                                        >
                                          Add
                                        </Button>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })
                    )}
                  </Paper>

                  {hasLoadedSource && sourceTotalPages > 1 && (
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      sx={{ mt: 1.25 }}
                    >
                      <Pagination
                        count={sourceTotalPages}
                        page={sourcePage}
                        color="primary"
                        size="small"
                        onChange={(event, value) => fetchSourceDocs(value)}
                      />
                    </Stack>
                  )}
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      height: 420,
                      width: "100%",
                      "& .super-app-theme--header": {
                        backgroundColor: "#ebf5ff",
                        color: "#0d3557",
                        fontWeight: 800,
                        fontSize: 12,
                      },
                      "& .MuiDataGrid-cell": {
                        fontSize: 12,
                        py: 0.25,
                      },
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        {
                          outline: "1px solid #1976d2",
                        },
                    }}
                  >
                    <DataGrid
                      rows={returnRows}
                      columns={returnColumns}
                      getRowId={(row) => row.srl}
                      density="compact"
                      rowHeight={30}
                      headerHeight={32}
                      disableSelectionOnClick
                      components={{
                        Toolbar: () => (
                          <GridTopBar
                            rowCount={returnRows.length}
                            title="Warranty Claim Return Rows"
                          />
                        ),
                      }}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: "srl", sort: "asc" }],
                        },
                        pagination: {
                          pageSize: 10,
                        },
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1} justifyContent="center">
                {mode === 1 && (
                  <Button
                    disabled={disableSave}
                    onClick={handleSave}
                    size="medium"
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                  >
                    Save
                  </Button>
                )}

                {mode === 2 && (
                  <Button
                    disabled={disableUpdate}
                    onClick={handleUpdate}
                    size="medium"
                    variant="contained"
                    color="warning"
                    sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                  >
                    Update
                  </Button>
                )}
              </Stack>
            </Box>
          }
        />
        {redirectToLoginPage()}
      </Box>
    </>
  );
};

export default WarrantyClaimReturn;